import Button from "@components/common/Button";
import Notification from "@components/common/Notification";
import SelectInput, { ReactSelectValue } from "@components/form/SelectInput";
import { TextInputDefault } from "@components/form/TextInput"
import { API_URL } from "@redux/api";
import { EditItemFormType, validateEditItem } from "@redux/validations/createItems";
import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { NotificationType } from "react-app-env";

type ErrorsType = {
  name?: string;
  category?: string;
  code?: string;
  quantity?: string;
  unit?: string;
  location?: string;
}

const EditItemForm = ({ data, onClose }: {
  data: EditItemFormType | null;
  onClose: () => void;
}) => {

  const [id, setId] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [category, setCategory] = useState<any>(null);
  const [code, setCode] = useState<string>("");
  const [location, setLocation] = useState<string>("");
  const [quantity, setQuantity] = useState<string>("");
  const [unit, setUnit] = useState<string>("");
  const [recentImage, setRecentImage] = useState<string | null>(null);

  const [imageFiles, setImageFiles] = useState<any>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [notification, setNotification] = useState<NotificationType | null>(null);
  const [errors, setErrors] = useState<ErrorsType>({});

  const [categoryOptions, setCategoryOptions] = useState<ReactSelectValue[] | []>([]);

  useEffect(() => {
    if(data) {
      const { _id, name, code, category, location, quantity, unit, image } = data;

      const categorySelectValue = {
        value: category._id,
        label: category.name.charAt(0).toUpperCase() + category.name.slice(1)
      };

      setId(_id);
      setName(name);
      setCode(code || "");
      setCategory(categorySelectValue);
      setLocation(location || "");
      setQuantity(quantity.toString());
      setUnit(unit || "");
      setRecentImage(image);
    };

    return () => {}
  }, [data]);
  

  const restoreDefaultValues = () => {
    setErrors({});
    setName("");
    setCategory(null);
    setCode("");
    setLocation("");
    setQuantity("0");
    setUnit("");
    setImageFiles([]);
  };

  const uploadImages = async ({ images, file_name, category }: { images: any; file_name: string, category: string }) => {
    if(images.length === 0) {
      return undefined;
    } else {
      const formData = new FormData();
  
      const fileType = images[0]["type"].split("/");
      const ext = fileType[fileType.length - 1];
      const filename = `${file_name}.${ext}`;
  
      formData.append("item_category", category.toLowerCase());
      formData.append("inventory_image", images[0], filename);
  
      const response = await axios.post(`${API_URL}/upload/inventory-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
  
      const { status, data } = response;
      if (status !== 200) return null;
      return data.data.filename;
    }
  };

  const fetchCategoryOptions = useCallback(async () => {
    setIsLoading(true);
    const response = await axios.get(`${API_URL}/inventory/category/options`);

    const { status, data } = response;

    if (status === 200) {
      setCategoryOptions(data.data)
      setIsLoading(false);
    } else {
      setNotification({
        isError: true,
        title: "Error",
        text: data.message,
      })
    }
  }, []);

  useEffect(() => {
    fetchCategoryOptions();
    return () => { }
  }, [fetchCategoryOptions]);

  const submitCreateItem = async(payload: EditItemFormType) => {
    const { 
      _id,
      name,
      code,
      category,
      quantity,
      location,
      unit,
      image
    } = payload;

    const dataToSubmit = {
      _id,
      name,
      code: code || null,
      category: category?.value,
      quantity,
      location: location || null,
      unit,
      image
    };

    const response = await axios.put(`${API_URL}/inventory/item/${id}`, dataToSubmit);

    const { status } = response;

    if(status === 200) {
      setIsLoading(false);
      setNotification({
        isError: false,
        title: "Sukses!",
        text: "Berhasil memperbarui data barang",
      });
      
      setTimeout(() => {
        onClose();
        restoreDefaultValues();
        setNotification(null);
      }, 3000);
    } else {
      setIsLoading(false);
      setNotification({
        isError: true,
        title: "Error",
        text: "Terjadi kesalahan. Silahkan hubungi admin.",
      });

      return undefined;
    };
  };

  const isValidForm = (data: EditItemFormType) => {
    const { errors, isValid } = validateEditItem(data);
    if (!isValid) {
      setErrors(errors);
    }
    return isValid;
  };

  const uploadHandler = async (e: any) => {
    e.preventDefault();
    setIsLoading(true);
    setNotification(null);
    setErrors({});

    const payload = {
      _id: id,
      name,
      category,
      code,
      quantity: Number(quantity),
      unit,
      location,
      image: imageFiles.length > 0 ? null : recentImage
    };

    if(isValidForm(payload)) {
      if(imageFiles.length > 0) {
        const imageFilename = await uploadImages({
          images: imageFiles,
          file_name: payload.name.toLowerCase().split(" ").join("-"),
          category: category.label
        });
    
        if (!imageFilename) {
          submitCreateItem(payload);
          return undefined;
        } else {
          payload["image"] = imageFilename;
          submitCreateItem(payload);
        };
      } else {
        submitCreateItem(payload);
      }
    } else {
      setIsLoading(false);
      setNotification({
        isError: true,
        title: "Data tidak lengkap",
        text: "Mohon lengkapi formulir di atas",
      });
    };
  };

  return (
    <form>
      <div className="mt-4">
        <TextInputDefault
          name="name"
          label="Nama Barang"
          value={name}
          placeholder="Masukkan nama barang"
          onChange={({ target }) => {
            setErrors({
              ...errors,
              [target.name]: null,
            });
            setName(target.value);
          }}
          disabled={isLoading}
          error={errors.name}
        />
      </div>
      <div className="mt-4">
        <SelectInput
          isMulti={false}
          label='Kategori'
          placeholder="Pilih kategori barang"
          disabled={isLoading}
          options={categoryOptions}
          value={category}
          error={errors?.category}
          onChange={(value) => setCategory(value)}
        />
      </div>
      <div className="mt-5">
        <TextInputDefault
          name="code"
          label="Kode Barang"
          value={code}
          placeholder="Masukkan kode barang ( jika ada )"
          onChange={({ target }) => {
            setErrors({
              ...errors,
              [target.name]: null,
            });
            setCode(target.value);
          }}
          disabled={isLoading}
          error={errors?.code}
        />
      </div>
      <div className="mt-5">
        <TextInputDefault
          name="location"
          label="Lokasi Barang"
          value={location}
          placeholder="Masukkan lokasi ( jika ada )"
          onChange={({ target }) => {
            setErrors({
              ...errors,
              [target.name]: null,
            });
            setLocation(target.value);
          }}
          disabled={isLoading}
          error={errors?.location}
        />
      </div>
      <div className="flex items-center mt-4 gap-4">
        <div>
          <TextInputDefault
            name="quantity"
            label="Jumlah Barang"
            type="number"
            value={quantity}
            placeholder="Masukkan jumlah"
            onChange={({ target }) => {
              setErrors({
                ...errors,
                [target.name]: null,
              });
              setQuantity(target.value);
            }}
            disabled={isLoading}
            error={errors?.quantity}
          />
        </div>
        <div>
          <TextInputDefault
            name="unit"
            label="Satuan"
            value={unit}
            placeholder="pcs / krat / dsb."
            onChange={({ target }) => {
              setErrors({
                ...errors,
                [target.name]: null,
              });
              setUnit(target.value);
            }}
            disabled={isLoading}
            error={errors?.code}
          />
        </div>
      </div>
      <div className="mt-5">
        <label className="text-sm font-semibold ml-1 mb-1">Gambar Barang</label>
        {recentImage && category ? 
          <div className="flex flex-col items-start justify-center w-full mt-1 p-4">
            <img 
              alt=""
              src={`${API_URL}/images/inventories/${category.label.toLowerCase()}/${recentImage}`}
              className="w-[120px]"
            />
            <Button
              buttonType="error"
              onClick={() => {
                setImageFiles([]);
                setRecentImage(null);
              }}
              rounded={false}
              className='w-full justify-center mt-2 text-sm font-normal'
            >
              Ganti Gambar
            </Button>
          </div>
          : imageFiles.length > 0 ? 
            <>
              <img 
                alt=""
                src={URL.createObjectURL(imageFiles[0])}
                className="w-[120px]"
              />
              <Button
                buttonType="error"
                onClick={() => setImageFiles([])}
                rounded={false}
                className='w-full justify-center mt-2 text-sm font-normal'
              >
                Ganti Gambar
              </Button>
            </>
          : <div className="flex items-center justify-center w-full mt-1">
            <label htmlFor="dropzone-file" className="flex flex-col items-center justify-center w-full h-50px border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <svg className="w-8 h-8 mb-4 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                  <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2" />
                </svg>
                <p className="mb-2 text-sm text-gray-500"><span className="font-semibold">Klik untuk Pilih Gambar</span></p>
                <p className="text-xs text-gray-500">Format Gambar : PNG atau JPG</p>
              </div>
              <input
                id="dropzone-file"
                type="file"
                className="hidden"
                onChange={(e) => setImageFiles(e.target.files)}
              />
            </label>
          </div>
        }
      </div>
      <div className="mt-4">
        <Notification notification={notification} />
      </div>
      <div className="mt-8">
        { isLoading ? 
          <div>
            
          </div>
          : <button
            type='submit'
            onClick={uploadHandler}
            className='w-full justify-center bg-orange-600 text-white rounded-lg py-3 font-semibold'
          >
            Simpan
          </button>
        }
      </div>
    </form>
  )
}

export default EditItemForm